import { Group, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useStatusLabels } from '../../../../../contexts/status-labels.context';
import { useFlowByPhaseTabStore, useRelatedTasksTabStore } from '../../../../../store/strategy-store/strategy-store';
import {
  setRelatedTasksSelectedEpic,
  setRelatedTasksSelectedPhase,
} from '../../../../../store/strategy-store/strategy-store.actions';
import { inkLight, secondaryBase } from '../../../../../styles/design-tokens';
import { Divider } from '../../../../../ui-library/divider/divider';
import { Radio } from '../../../../../ui-library/radio/radio';
import { Select } from '../../../../../ui-library/select/select';
import { Text } from '../../../../../ui-library/typography/typography';
import type { Phase } from '../../flow-by-phase-tab/flow-by-phase-tab.type';
import { isEnabledEpicSearch } from '../../initiative-performance.helpers';
import { useContributingEpics } from '../../initiative-performance.hooks';
import { FiltersSearch } from './filters-search';
import { getPhaseOptions } from './filters.helpers';
import { useFilteredEpics } from './filters.hooks';

/**
 * Side-panel filter controls for the related-tasks tab: a status/phase select
 * plus a searchable epic radio list.
 *
 * Phase options come from `getPhaseOptions`, which is handed the status-label
 * resolver so sub-group options display each board's source casing while their
 * `value`s stay the raw keys written to the related-tasks store.
 *
 * @returns {JSX.Element} The phase select and epic list for the filters panel
 */
export const FiltersEpics = () => {
  const i18n = useTranslation();
  const epics = useContributingEpics();
  const filteredEpics = useFilteredEpics();
  const selectedPhase = useRelatedTasksTabStore((state) => state.selectedPhase || 'all');
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const useGroups = useRelatedTasksTabStore((state) => state.useGroups);
  // Reactive subscription to the Flow-by-Phase store's `phases` so the
  // phase filter dropdown re-renders when overrides change.
  const phases = useFlowByPhaseTabStore((state) => state.phases);
  const { getStatusLabel } = useStatusLabels();

  const phaseOptions = getPhaseOptions(useGroups, i18n.t, phases ?? undefined, getStatusLabel);

  const onPhaseChange = (value: string) => {
    if (value === 'all') {
      setRelatedTasksSelectedPhase(null);
    } else {
      setRelatedTasksSelectedPhase(value as Phase);
    }
  };

  return (
    <Stack>
      {isEnabledEpicSearch(epics) && (
        <>
          <FiltersSearch />
          <Divider />
        </>
      )}
      <Select options={phaseOptions} value={selectedPhase} onChange={onPhaseChange} />
      <Divider />
      {epics.length > 0 && (
        <>
          <Group justify="space-between">
            <Radio
              key="all-epics"
              label={<Text size="small">{i18n.t('strategy.filters.all_epics')}</Text>}
              checked={selectedEpic === null}
              onChange={() => setRelatedTasksSelectedEpic(null)}
            />
            <Text size="small" color={inkLight}>
              {epics.length}
            </Text>
          </Group>
          <Divider />
        </>
      )}
      <Stack gap={24}>
        {filteredEpics.map((epic) => (
          <Stack gap={8} key={epic.id}>
            <Group justify="space-between">
              <Radio
                label={
                  <Text size="small" color={secondaryBase} style={{ lineHeight: '20px' }}>
                    {epic.title}
                  </Text>
                }
                checked={epic.id === selectedEpic}
                onChange={() => setRelatedTasksSelectedEpic(epic.id)}
              />
              <Text size="small" color={inkLight}>
                {`${epic.subtasks_count} ${i18n.t('common.work_unit').toLowerCase()}${epic.subtasks_count === 1 ? '' : 's'}`}
              </Text>
            </Group>
            <Text size="small">{epic.name}</Text>
          </Stack>
        ))}
      </Stack>
      {epics.length === 0 && (
        <Text size="small" color="dimmed">
          {i18n.t('strategy.filters.no_epics_found')}
        </Text>
      )}
    </Stack>
  );
};
